import { useEffect, useState } from "react";
import { Phone, Mail, Trash2, StickyNote, Loader2, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS = [["new", "Nouveau"], ["qualified", "Qualifié"], ["contacted", "Contacté"], ["won", "Gagné"], ["lost", "Perdu"]];
const QUALITY = [["", "—"], ["hot", "Chaud"], ["warm", "Tiède"], ["cold", "Froid"]];
const STATUS_COLOR = { new: "bg-blue-100 text-blue-700", qualified: "bg-amber-100 text-amber-700", contacted: "bg-purple-100 text-purple-700", won: "bg-green-100 text-green-700", lost: "bg-red-100 text-red-700" };
const QUALITY_COLOR = { hot: "text-red-600", warm: "text-amber-600", cold: "text-blue-500" };

export default function LeadsAdmin() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/leads").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/agents").then((r) => setAgents(r.data)); }, []);

  const update = async (lead, patch) => {
    await api.put(`/admin/leads/${lead.id}`, patch);
    setItems((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...patch } : l)));
    if (active?.id === lead.id) setActive({ ...active, ...patch });
  };
  const remove = async (lead) => { if (!window.confirm("Supprimer ce lead ?")) return; await api.delete(`/admin/leads/${lead.id}`); toast.success("Supprimé"); load(); };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/leads/${active.id}/notes`, { text: note });
      setActive({ ...active, notes: [...(active.notes || []), data] });
      setItems((prev) => prev.map((l) => (l.id === active.id ? { ...l, notes: [...(l.notes || []), data] } : l)));
      setNote("");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const filtered = filter ? items.filter((l) => l.status === filter) : items;
  const agentName = (id) => agents.find((a) => a.id === id)?.name || "";

  return (
    <div>
      <h1 className="font-heading text-3xl font-black text-ink">Leads — gare de triage</h1>
      <p className="text-ink/50 mt-1">Qualifiez, attribuez et suivez chaque demande.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${filter === "" ? "bg-primary text-white border-primary" : "border-border"}`}>Tous ({items.length})</button>
        {STATUS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${filter === v ? "bg-primary text-white border-primary" : "border-border"}`}>{l} ({items.filter((i) => i.status === v).length})</button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {filtered.map((l) => (
          <div key={l.id} className="bg-card border border-border rounded-xl p-4" data-testid={`lead-${l.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold text-ink">{l.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[l.status]}`}>{STATUS.find((s) => s[0] === l.status)?.[1]}</span>
                  {l.quality && <Flame size={15} className={QUALITY_COLOR[l.quality]} />}
                  <span className="text-xs text-ink/40">· {l.source}</span>
                </div>
                <div className="mt-1 flex items-center gap-4 text-sm text-ink/60 flex-wrap">
                  {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone size={13} /> {l.phone}</a>}
                  {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Mail size={13} /> {l.email}</a>}
                  {l.service && <span>{l.service}</span>}
                </div>
                {l.message && <p className="mt-1 text-sm text-ink/50 line-clamp-1">{l.message}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select value={l.status} onChange={(e) => update(l, { status: e.target.value })} className="text-xs border border-border rounded-lg px-2 py-1.5">{STATUS.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}</select>
                <select value={l.quality || ""} onChange={(e) => update(l, { quality: e.target.value })} className="text-xs border border-border rounded-lg px-2 py-1.5">{QUALITY.map(([v, lab]) => <option key={v} value={v}>{lab || "Qualité"}</option>)}</select>
                <select value={l.assigned_to || ""} onChange={(e) => update(l, { assigned_to: e.target.value })} className="text-xs border border-border rounded-lg px-2 py-1.5 max-w-[120px]"><option value="">Attribuer…</option>{agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
                <button onClick={() => { setActive(l); }} data-testid={`lead-notes-${l.id}`} className="inline-flex items-center gap-1 text-xs border border-border rounded-lg px-2 py-1.5 hover:border-primary"><StickyNote size={13} /> {l.notes?.length || 0}</button>
                {user?.role === "admin" && <button onClick={() => remove(l)} className="p-1.5 text-red-600"><Trash2 size={15} /></button>}
              </div>
            </div>
            {l.assigned_to && <p className="mt-2 text-xs text-ink/40">Attribué à {agentName(l.assigned_to)}</p>}
          </div>
        ))}
        {!filtered.length && <p className="text-center text-ink/40 py-10">Aucun lead.</p>}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Historique — {active?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {(active?.notes || []).map((n) => (
              <div key={n.id} className="bg-muted/40 rounded-lg p-3 text-sm">
                <p className="text-ink/80">{n.text}</p>
                <p className="text-xs text-ink/40 mt-1">{n.author} · {new Date(n.created_at).toLocaleString("fr-FR")}</p>
              </div>
            ))}
            {!active?.notes?.length && <p className="text-sm text-ink/40">Aucune note pour le moment.</p>}
          </div>
          <div className="flex gap-2 pt-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Ajouter une note de traitement…" className="flex-1 rounded-lg border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" data-testid="lead-note-input" />
            <button onClick={addNote} disabled={saving} className="bg-primary text-white px-4 rounded-lg font-bold text-sm disabled:opacity-60">{saving ? <Loader2 className="animate-spin" size={16} /> : "Ajouter"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
